import type { AuthChangeEvent, SupabaseClient } from '@supabase/supabase-js'
import { useCallback } from 'react'
import storage from 'local-storage-fallback';

import { useTracking } from '@/hooks/useTracking'
import type { Database } from '@/types/database'
import { LS_DISCOUNT_TIMER_KEY, LS_NEW_CHAR_KEY, LS_SIGNOUT_DATE_KEY } from '@/lib/constants'

interface EventStorageItem {
  t?: number;
}

const STORAGE_KEYS_TO_PRESERVE: string[] = [
  LS_DISCOUNT_TIMER_KEY,
  LS_NEW_CHAR_KEY
];
const DATED_KEY_LIFESPAN = 1000 * 60 * 60 * 24 * 60; // 60 days

export function useSupabaseAuthEvents(supabase: SupabaseClient<Database>) {
  const { /*phIdentify,*/ phReset } = useTracking(); // identified in useUserInfo

  const listenToAuthEvents = useCallback(() => {
    const { data } = supabase.auth.onAuthStateChange((event: AuthChangeEvent/*, session: Session | null*/) => {
      if (event === 'SIGNED_OUT') {
        phReset();
        [storage, window.sessionStorage].forEach((store) => {
          Object.entries(store).forEach(([key]) => {
            if (STORAGE_KEYS_TO_PRESERVE.includes(key)) {
              const item: EventStorageItem = JSON.parse(store?.getItem(key) ?? '{}') as EventStorageItem;
              const expired = ((item.t || Date.now()) + DATED_KEY_LIFESPAN) < Date.now();
              if (!expired || typeof item === 'boolean') return;
            }
            store?.removeItem(key);
          })
        });
        // so we can detect this browser had an active user for customization
        storage.setItem(LS_SIGNOUT_DATE_KEY, JSON.stringify({ t: Date.now() }));
      }
    });

    return data.subscription;
  }, [phReset, supabase]);

  return {
    listenToAuthEvents
  }
}